import { formatUploadDate } from './uploadDate';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Link,
  Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Rating from '@mui/material/Rating';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
  getMetadata,
} from 'firebase/storage';
import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { storage, db } from '../service/Firebase-config';
import { updateDoc, doc, getDoc } from 'firebase/firestore';
import { stringAvatar } from '../common/util';
import DeleteConfirmation from '../common/DeleteConfirmation';
import { useAuth } from '../service/AuthContext';
import { useGlobalContext } from '../context/GlobalContext';
import { useHomeworkContext } from '../context/HomeworkContext';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const HomeworkUploads = ({ fileStudent, setFileStudent }) => {
  const { selectedUser, setTotalStars } = useGlobalContext();
  const { selectedHw, rating, setRating } = useHomeworkContext();
  const { userInfo } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isPdfFile, setIsPdfFile] = useState(false);

  const homeworkRef = useMemo(() => {
    if (!selectedUser?.uid || !selectedHw?.id) return null;
    return doc(db, `students/${selectedUser.uid}/homeworks/`, selectedHw.id);
  }, [selectedUser?.uid, selectedHw?.id]);

  useEffect(() => {
    const patchMissingCreatedDate = async () => {
      if (!homeworkRef || !selectedUser?.uid || !selectedHw?.id || !fileStudent?.length) {
        return;
      }

      const filesMissingCreatedDate = fileStudent.filter((file) => !file.createdDate);
      if (filesMissingCreatedDate.length === 0) return;

      setIsLoading(true);

      try {
        const patchedFiles = await Promise.all(
          fileStudent.map(async (file) => {
            if (file.createdDate) return file;

            try {
              const studentFileRef = ref(
                storage,
                `file/${selectedUser.uid}/${selectedHw.id}/${file.savedName}`
              );

              const metadata = await getMetadata(studentFileRef);

              return {
                ...file,
                createdDate: metadata.timeCreated || null,
              };
            } catch (error) {
              console.error('Error loading metadata for file:', file.fileName, error);
              return file;
            }
          })
        );

        const hasAnyPatched = patchedFiles.some(
          (file, index) => !fileStudent[index]?.createdDate && file.createdDate
        );

        if (hasAnyPatched) {
          await updateDoc(homeworkRef, { fileStudent: patchedFiles });
          setFileStudent(patchedFiles);
        }
      } catch (error) {
        console.error('Error patching missing createdDate values:', error);
      } finally {
        setIsLoading(false);
      }
    };

    patchMissingCreatedDate();
  }, [fileStudent, homeworkRef, selectedUser?.uid, selectedHw?.id, setFileStudent]);

  const handleFileUpload = async (e) => {
    setIsLoading(true);

    const file = e.target.files[0];
    const allowedTypes = ['application/pdf', 'application/zip', 'application/x-zip-compressed'];

    if (!allowedTypes.includes(file?.type)) {
      setIsPdfFile(true);
      setIsLoading(false);
      return;
    }

    if (!homeworkRef) {
      setIsLoading(false);
      return;
    }

    const id = uuidv4();
    const savedName = `${id}${file.name}`;
    const studentFileRef = ref(storage, `file/${selectedUser.uid}/${selectedHw.id}/${savedName}`);

    try {
      const snapshot = await uploadBytes(studentFileRef, file);
      const url = await getDownloadURL(snapshot.ref);

      const newFile = {
        fileName: file.name,
        fileUrl: url,
        savedName,
        createdDate: new Date(),
      };

      const updatedFileList = fileStudent ? [...fileStudent, newFile] : [newFile];

      await updateDoc(homeworkRef, { fileStudent: updatedFileList });
      setFileStudent(updatedFileList);
    } catch (error) {
      console.error('File upload failed:', error);
    } finally {
      e.target.value = '';
      setIsLoading(false);
    }
  };

  const handleRemoveFile = async (file) => {
    if (!homeworkRef) return;

    setIsLoading(true);

    const studentFileRef = ref(
      storage,
      `file/${selectedUser.uid}/${selectedHw.id}/${file.savedName}`
    );

    try {
      await deleteObject(studentFileRef);

      const updatedFileList = (fileStudent || []).filter(
        (item) => item.savedName !== file.savedName
      );

      setFileStudent(updatedFileList);
      await updateDoc(homeworkRef, { fileStudent: updatedFileList });
    } catch (error) {
      console.error('Error removing file:', error);
    } finally {
      setSelectedFile(null);
      setIsLoading(false);
    }
  };

  const handleUpdateRating = async (newValue) => {
    if (!homeworkRef || !selectedUser?.uid) return;

    const studentRef = doc(db, 'students', selectedUser.uid);
    const studentSnapshot = await getDoc(studentRef);

    if (studentSnapshot.exists()) {
      const studentData = studentSnapshot.data();
      const currentTotalStars = studentData.totalStars || 0;

      const homeworkSnapshot = await getDoc(homeworkRef);
      if (homeworkSnapshot.exists()) {
        const homeworkData = homeworkSnapshot.data();
        const currentRating = homeworkData.rating || 0;
        const newTotalStars = currentTotalStars + newValue - currentRating;

        await updateDoc(homeworkRef, { rating: newValue });
        await updateDoc(studentRef, { totalStars: newTotalStars });

        setRating(newValue);
        setTotalStars(newTotalStars);
      }
    }
  };

  const handleClearRating = () => {
    handleUpdateRating(0);
  };

  return (
    <React.Fragment>
      <Grid container spacing={1}>
        <Grid item xs={12} md={5} lg={6}>
          <Grid container direction="column" alignItems="center" gap={2}>
            <Typography>{selectedUser.displayName}</Typography>
            <Avatar
              src={selectedUser.photoUrl || undefined}
              {...(!selectedUser.photoUrl && stringAvatar(selectedUser.displayName))}
              sx={{ width: 60, height: 60 }}
            />
            <Rating
              name="simple-controlled"
              value={rating}
              onChange={(event, newValue) =>
                userInfo?.role === 'admin' && handleUpdateRating(newValue)
              }
              size="large"
              readOnly={userInfo?.role !== 'admin'}
            />
            {userInfo?.role === 'admin' && (
              <Button onClick={handleClearRating} variant="outlined">
                Clear
              </Button>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12} md={7} lg={6} container direction="column" alignItems="center" gap={2}>
          <Tooltip
            title={
              <React.Fragment>
                <Typography color="inherit">File Upload Rules</Typography>
                {'- Only PDF and Zip files are allowed'}
                <br />
                {'- Maximum two files can be uploaded'}
              </React.Fragment>
            }
            arrow
            placement="top"
          >
            <Button
              disabled={fileStudent?.length >= 2 || userInfo?.role !== 'student'}
              component="label"
              variant="contained"
              startIcon={<CloudUploadIcon />}
            >
              Upload file
              <VisuallyHiddenInput type="file" accept=".pdf,.zip" onChange={handleFileUpload} />
            </Button>
          </Tooltip>

          {fileStudent &&
            fileStudent.map((file) => (
              <Grid
                key={file.savedName}
                item
                width="80%"
                p={1}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 1,
                  '&:hover': { backgroundColor: '#e3f2fd' },
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Typography>
                    <Link href={file.fileUrl} target="_blank" underline="hover">
                      {file.fileName}
                    </Link>
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {formatUploadDate(file.createdDate)}
                  </Typography>
                </div>

                {userInfo?.role !== 'admin' && (
                  <IconButton color="inherit" size="small" onClick={() => setSelectedFile(file)}>
                    <DeleteRoundedIcon />
                  </IconButton>
                )}
              </Grid>
            ))}

          {isLoading && <Typography variant="h5">Loading ...</Typography>}
        </Grid>
      </Grid>

      <DeleteConfirmation
        open={Boolean(selectedFile)}
        closeDelete={() => setSelectedFile(null)}
        handleDelete={() => handleRemoveFile(selectedFile)}
        title="Are you sure you want to delete this file?"
      />

      <Dialog open={isPdfFile} onClose={() => setIsPdfFile(false)}>
        <DialogTitle>File Upload Conflicts</DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>- Only PDF and Zip files are allowed</Typography>
          <Typography gutterBottom>- Maximum two files can be uploaded</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPdfFile(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default HomeworkUploads;